import { memo, useCallback } from 'react';
import { IconButton, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Stack } from '@mui/system';

import { StyledTooltip } from '@/modules/common/components/StyledTooltip';

import { Icon } from '../Icon';
import { ToolbarItemType, ToolbarToolItem, ToolId } from '../types';
import { styles } from '../styles';

type Props = {
  tool: ToolbarToolItem;
  onSelectTool: (toolId: ToolId) => void;
  isSelected: boolean;
  variant: ToolbarItemType;
  disabled?: boolean;
};

function ToolButtonComponent({
  tool,
  onSelectTool,
  isSelected,
  variant = 'tool',
  disabled = false,
}: Props) {
  const { t } = useTranslation(['interface']);

  const handleClick = useCallback(() => onSelectTool(tool.id), [onSelectTool, tool.id]);

  if (variant === 'menu') {
    return (
      <Stack
        style={isSelected ? styles.menuButtonSelected : {}}
        sx={{
          width: '100%',
          opacity: disabled ? 0.5 : 1,
          pointerEvents: disabled ? 'none' : 'auto',
          '&:hover': {
            color: (theme) => theme.palette.neutral.darker,
            backgroundColor: (theme) => theme.palette.neutral.greyLight,
          },
        }}
        onClick={handleClick}
      >
        <Stack style={styles.row}>
          <Stack style={styles.rowInner}>
            <IconButton
              sx={{
                marginLeft: -1,
                pointerEvents: 'none',
                color: 'inherit',
              }}
              style={styles.iconButton}
              tabIndex={-1}
              disableRipple
              disableFocusRipple
              disableTouchRipple
              size='medium'
            >
              <Icon icon={tool.icon} />
            </IconButton>

            <Typography color='inherit'>{t(tool.label)}</Typography>
          </Stack>

          {tool.shortcut && <Typography style={styles.shortcut}>{tool.shortcut}</Typography>}
        </Stack>
      </Stack>
    );
  }

  return (
    <StyledTooltip title={t(tool.tooltip)} placement='right'>
      <span>
        <IconButton
          style={isSelected ? styles.menuButtonSelected : styles.iconButton}
          onClick={handleClick}
          disabled={disabled}
          disableRipple
          disableFocusRipple
          disableTouchRipple
          size='medium'
        >
          <Icon icon={tool.icon} />
        </IconButton>
      </span>
    </StyledTooltip>
  );
}

export const ToolButton = memo(ToolButtonComponent);
